import { api } from '../lib/api';
import { formatAgo, formatDateTime } from '../lib/format';
import { go } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { AccountLink, FrameLink, Loading, Pager, Pill } from '../components/bits';
import { PageHead } from '../components/Shell';

interface Props {
  readonly token: string;
  readonly query: URLSearchParams;
}

export const Screens = ({ token, query }: Props) => {
  const revoked = query.get('revoked') === '1';
  const offset = Number(query.get('offset')) || 0;
  const loaded = useLoad(
    () => api.listScreens(token, { revoked: revoked ? '1' : undefined, offset }),
    [token, revoked, offset],
  );
  const now = Date.now();

  return (
    <>
      <PageHead
        eyebrow="Screens"
        title="Every paired screen"
        lede="A television, a tablet or a browser tab holding a display token. Most recently seen first."
        actions={
          <label className="check">
            <input
              type="checkbox"
              checked={revoked}
              onChange={(event) => go('/screens', { revoked: event.target.checked ? '1' : undefined })}
            />
            Include disconnected
          </label>
        }
      />

      <Loading loaded={loaded}>
        {(page) => (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Screen</th>
                    <th scope="col">Frame</th>
                    <th scope="col">Owner</th>
                    <th scope="col">Paired</th>
                    <th scope="col">Last seen</th>
                    <th scope="col">State</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((screen) => {
                    const quiet = screen.lastSeenAt === null || now - screen.lastSeenAt > 86_400_000;
                    return (
                      <tr key={screen.id}>
                        <td>{screen.label}</td>
                        <td>
                          <FrameLink device={screen.device} />
                        </td>
                        <td>
                          <AccountLink account={screen.owner} />
                        </td>
                        <td>{formatDateTime(screen.createdAt)}</td>
                        <td>{formatAgo(screen.lastSeenAt, now)}</td>
                        <td>
                          {screen.revokedAt !== null ? (
                            <Pill tone="bad">disconnected</Pill>
                          ) : quiet ? (
                            <Pill tone="wait">quiet</Pill>
                          ) : (
                            <Pill tone="ok">polling</Pill>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager
              page={page}
              noun="screens"
              onOffset={(next) => go('/screens', { revoked: revoked ? '1' : undefined, offset: next })}
            />
          </>
        )}
      </Loading>
    </>
  );
};
